import { addMilliseconds, addMinutes, isPast } from "date-fns";
import {
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { toast } from "react-toastify";
import { ShareDBSourceOptions } from "sharedb";
import { Callback, Doc, Op } from "sharedb/lib/client";

import { ShareDBContext } from "../providers/ShareDBProvider";
import { AuthError, checkUserCanPerformOperation } from "../shared/auth";
import { AUTH_ERRORS, WebsocketErrorType } from "../shared/types/websocket-auth";
import {
  ExtendedDoc,
  ExtendedOp,
  HARD_ROLLBACK_STATUS_EVENT,
  WorksheetType,
} from "../shared/types/worksheet";
import { DATA_PATH } from "../shared/worksheets/constants";
import { accessIdHasData, hasBlocks } from "../shared/worksheets/doc-helpers";
import { createIncrementVersionQueue } from "../worksheets/field-versioning";

import {
  getDocErrorReason,
  isMissingReadPermissionError,
  logReadPermissionDenied,
} from "./read-permission-errors";
import {
  clearSubmissionTrackerById,
  createSubmissionTracker,
  getOperationId,
  getRelatedOperationIds,
  handleSubmissionComposed,
  reconcileSubmissionTrackersForDoc,
  updateSubmissionTracker,
} from "./submission-tracker";
import { markShareDBErrorReported, reportShareDBErrorOnce } from "./submit-error-reporting";
import {
  enrichShareDBFailureLog,
  logUncommittedOpsDiscardedByDocRefresh,
  reportUncommittedWorksheetFieldTextLoss,
  trackLocalOpFieldTextAfterApply,
  trackLocalOpFieldTextBeforeApply,
} from "./worksheet-field-content-client-log";

import { t } from "i18n/i18n";
import { usePageCapabilitiesForPageId } from "store/capability-hooks";
import { store, useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectCurrentUserId,
  selectPageCapabilities,
  selectPageGroupIdForCurrentUserByPageId,
  selectPageIdByWorksheetId,
  selectSectionCapabilities,
  selectWorksheetIdByPage,
} from "store/selectors";
import { viewStateActions } from "store/slices/view";
import { toastErrorMessage } from "utils/alerts";
import { isTestEnv } from "utils/env";
import { logInfoWithDebugFlag, rollbarAndLogError, rollbarAndLogWarning } from "utils/logger";
import { getUpdateDraftPublicationFunction } from "utils/publishing";

// ============================================================================
// Constants
// ============================================================================

// This sets how many listeners we can register for a single EventEmitter.
// Doc and DocPresence are both EventEmitters, and the default is 10.
// If the max number is exceeded, we just get a warning, nothing breaks.
// For our implementation, each Field listens to the doc individually,
// so we expect this number to be quite a lot higher than the default.
// The current number is chosen so that we likely get a warning if something actually goes
// wrong, e.g. listeners get added 5-10x more often than expected.
export const MAX_DOC_LISTENERS = 256;
export const MAX_RETRIES = 100;
export const MAX_MINUTES_IN_FLIGHT = 10;
export const MINIMUM_TIME_BETWEEN_REQUESTS_MS = 1000;
export const MAX_RETRY_DELAY_MS = 30000;
export const DELAYED_SAVE_WARNING_THRESHOLD_MS = 10000;
// Mirrors ShareDBError.CODES.ERR_HARD_ROLLBACK_FETCH_FAILED. ShareDB does not expose a typed
// client error-code API, so keep this synchronized when upgrading ShareDB.
const HARD_ROLLBACK_FETCH_FAILED_CODE = "ERR_HARD_ROLLBACK_FETCH_FAILED";

type ExtendedOpWithCallbacks = ExtendedOp & { callbacks?: Callback[] };

const callbackCount = (op: ExtendedOpWithCallbacks | null | undefined): number =>
  op?.callbacks?.length ?? 0;

const getHardRollbackCallbackCounts = (doc: ExtendedDoc) => {
  const inflightSubmitCallbackCount = callbackCount(doc.inflightOp);
  const pendingOpCount = doc.pendingOps.length;
  const pendingSubmitCallbackCount = doc.pendingOps.reduce(
    (total, op) => total + callbackCount(op),
    0,
  );

  return {
    inflightSubmitCallbackCount,
    pendingOpCount,
    pendingSubmitCallbackCount,
    discardedSubmitCallbackCount: inflightSubmitCallbackCount + pendingSubmitCallbackCount,
  };
};

/** Sets the status property and emits an event so existing subscribers can react. */
const publishHardRollbackStatus = (
  doc: ExtendedDoc,
  status: NonNullable<ExtendedDoc["hardRollbackStatus"]>,
) => {
  Object.assign(doc, { hardRollbackStatus: status });
  doc.emit(HARD_ROLLBACK_STATUS_EVENT, status);
};

/** Tracks one hard rollback until ShareDB finishes restoring the document. */
export const createHardRollbackRecoveryTracker = (doc: ExtendedDoc) => {
  let startedAtMs: number | null = null;
  let operationId: string | undefined;
  let reason: string | undefined;

  const clear = () => {
    startedAtMs = null;
    operationId = undefined;
    reason = undefined;
  };

  return {
    start(nextOperationId: string | undefined, nextReason: string) {
      // Overlapping rollbacks are detected and logged, but not correlated yet. A later rollback
      // can overwrite this data before the earlier recovery completes.
      startedAtMs = Date.now();
      operationId = nextOperationId;
      reason = nextReason;
    },
    clear,
    complete() {
      if (doc.hardRollbackStatus !== "recovered" || startedAtMs === null) return;

      logInfoWithDebugFlag("debug_sharedb", "[ShareDBDoc] Hard rollback recovery completed", {
        docId: doc.id,
        operationId,
        durationMs: Date.now() - startedAtMs,
        reason,
      });
      clear();
    },
  };
};

// ============================================================================
// Core Infrastructure
// ============================================================================

export type DocCache = Record<string, ExtendedDoc> & {
  /** Latest worksheet doc. Used by AI tools. */
  current?: ExtendedDoc;
};

export const docCache = {} as DocCache;

export type DocUpdateMeta = {
  ops?: Op[];
  source?: unknown;
};

type UpdateCallback = (meta?: DocUpdateMeta) => void;

const cacheCallbacks: Record<string, Set<UpdateCallback>> = {};

const cacheHasNoListeners = (docId: string) => {
  const callbacks = cacheCallbacks[docId];
  return !callbacks || callbacks.size === 0;
};

/**
 * We use a small custom event emitter system to allow multiple components to listen to changes
 * originating from a single ShareDB document, without re-running side effects.
 */
const triggerDocUpdate = (docId: string, ops?: Op | Op[], source?: unknown) => {
  const callbacks = cacheCallbacks[docId];
  if (!callbacks) return;
  const meta: DocUpdateMeta = {
    ops: !ops ? [] : Array.isArray(ops) ? ops : [ops],
    source,
  };
  callbacks.forEach((cb) => cb(meta));
};

/** Keep submission trackers in sync with ShareDB's send queue after each doc op. */
const updateSubmissionTrackerState = (doc: ExtendedDoc, ops: ExtendedOp[], stage: string) => {
  const op = ops[0];
  // Local submit batches carry operationId; remote batches do not — reconcile handles those.
  if (op?.operationId) {
    updateSubmissionTracker(op.operationId, doc.pendingOps, stage);
  }
  reconcileSubmissionTrackersForDoc(doc, stage);
};

const listenToDocUpdates = (docId: string, callback: UpdateCallback) => {
  if (!cacheCallbacks[docId]) {
    cacheCallbacks[docId] = new Set();
  }
  const callbacks = cacheCallbacks[docId];
  callbacks.add(callback);
  return () => {
    callbacks.delete(callback);
  };
};

const checkOpPermissions = (doc: Doc<WorksheetType>, docOp: Op | Op[]) => {
  const state = store.getState();
  const currentUserId = selectCurrentUserId(state) ?? state.auth.websocket.user_id;
  const { canAuthorSection } = selectSectionCapabilities(state);
  const pageId = selectPageIdByWorksheetId(state, doc.id);
  const { canViewPage } = selectPageCapabilities(state, pageId);
  const pageGroupId = selectPageGroupIdForCurrentUserByPageId(state, pageId);

  try {
    const ops = Array.isArray(docOp) ? docOp : [docOp];

    for (const op of ops) {
      // Check if we have permissions
      checkUserCanPerformOperation(
        currentUserId,
        canAuthorSection,
        canViewPage,
        [pageGroupId].filter(Boolean) as string[],
        op,
        doc.data,
        true,
      );
    }
  } catch (err) {
    rollbarAndLogError("[ShareDBDoc] Failed client side op permission check", err, {
      op: JSON.stringify(docOp),
      docId: doc.id,
      pageId,
      currentUserId,
      canAuthorSection,
      canViewPage,
      pageGroupId,
    });
    // We ignore overwrite errors, as we assume the remote state (where the data exists),
    // is the correct one, and will be synced to the local state soon.
    if (!(err instanceof AuthError) || err.message !== AUTH_ERRORS.OVERWRITE_DISALLOWED) {
      throw err;
    }
  }
};

// If we want to refresh the doc because e.g. the permissions changed on the backend,
// but the doc is already cached by ShareDB, we need to fetch and re-ingest the data manually.
// Just calling connection.get(docId) will return the locally cached version.
//
// ShareDB doesn't have a function to re-fetch data, which is why we manually
// interfere with the ShareDB doc's internal state to make it believe a
// re-sync is necessary. This is brittle and mostly undocumented.
// I made this by looking at the ShareDB internal code and seeing what
// might be able to cause a refresh.
export const forceRefreshDoc = (docId: string, callback?: (err?: Error) => void) => {
  const cachedDoc = docCache[docId];
  if (!cachedDoc) {
    callback?.();
    return;
  }

  const state = store.getState();
  const pageId = selectPageIdByWorksheetId(state, docId);
  const { canViewPage } = selectPageCapabilities(state, pageId);
  if (!canViewPage) {
    logInfoWithDebugFlag(
      "debug_sharedb",
      "[ShareDBDoc] Skipping forceRefreshDoc: no view permission",
      { docId },
    );
    callback?.();
    return;
  }

  logUncommittedOpsDiscardedByDocRefresh(cachedDoc, "forceRefreshDoc");

  const docPrototype = cachedDoc as unknown as {
    _setType: (type: string) => void;
    version: number;
    _fetch: (options: { force: boolean }, cb: (err: Error) => void) => void;
  };
  // From reading the source code, these are the necessary changes to force a refresh.
  (docPrototype as { version: number | null }).version = null;
  (docPrototype as { _setType: (t: string | null) => void })._setType(null);

  docPrototype._fetch({ force: true }, (err) => {
    if (err) {
      if (isMissingReadPermissionError(err)) {
        logReadPermissionDenied(
          "[ShareDBDoc] Read permission denied while fetching doc",
          docId,
          err,
          canViewPage,
          "fetch",
        );
      } else {
        const websocket = store.getState().auth.websocket;
        const reason = getDocErrorReason(docId, canViewPage, "fetch");
        rollbarAndLogError("[ShareDBDoc] Error fetching doc", {
          docId,
          user_id: websocket?.user_id,
          err: err.message,
          reason,
        });
      }
    }
    triggerDocUpdate(docId);
    if (callback) {
      callback(err);
    }
  });
};

const { incrementVersion, flushQueue } = createIncrementVersionQueue(docCache);

/**
 * Stack frames to ignore when resolving useWorksheet's call site for debug logs.
 * Includes ShareDBDoc itself and React hook internals (useMemo / useState / renderWithHooks / …).
 */
const REACT_INTERNAL_STACK_FNS = new Set([
  "mountState",
  "useState",
  "useMemo",
  "mountMemo",
  "updateMemo",
  "useReducer",
  "mountReducer",
  "updateReducer",
  "updateState",
  "dispatchSetState",
  "basicStateReducer",
  "renderWithHooks",
  "updateFunctionComponent",
  "mountFunctionComponent",
]);

const isIgnoredCallerStackFrame = (line: string, fnName: string | undefined): boolean => {
  if (line.includes("ShareDBDoc.tsx")) return true;
  if (fnName && REACT_INTERNAL_STACK_FNS.has(fnName)) return true;
  if (/[/\\]node_modules[/\\](?:\.vite[/\\]deps[/\\])?(?:react-dom|react|scheduler)\b/.test(line)) {
    return true;
  }
  if (/\b(?:react-dom|react)(?:\.development|\.production)?(?:\.js)?\b/.test(line)) return true;
  return false;
};

/**
 * Extracts caller information from stack trace for debugging.
 * TODO: move this to a shared utility function and use it in other places.
 */
const getCallerInfo = (): string | undefined => {
  try {
    const stack = new Error().stack;
    if (!stack) return undefined;
    const lines = stack.split("\n");
    // Walk past Error / getCallerInfo / useWorksheet / React hook frames.
    for (let i = 1; i < Math.min(lines.length, 25); i += 1) {
      const line = lines[i];
      if (line) {
        // Prefer "Object.useMemo" → useMemo; fall back to first identifier.
        const match = line.match(/at\s+(?:(\w+)\.)?(\w+)/);
        const fnName = match?.[2];
        if (fnName && !isIgnoredCallerStackFrame(line, fnName)) {
          return fnName;
        }
      }
    }
  } catch {
    // Ignore errors in stack trace parsing
  }
  return undefined;
};

// ============================================================================
// Core useWorksheet Hook
// ============================================================================

/**
 * useWorksheet will connect to a remote ShareDB document and return the doc object,
 * and will also force a re-render when any of the document data changes.
 */
export type CheckIfRerenderIsNeededFn = (
  doc: ExtendedDoc | null,
  previousDoc: ExtendedDoc | null,
  meta?: DocUpdateMeta,
) => boolean;

export const useWorksheet = (
  docId: string | undefined | null,
  checkIfRerenderIsNeeded?: CheckIfRerenderIsNeededFn,
) => {
  // Debug call site for logs — computed once per mount, stable for effect deps.
  const caller = useMemo(() => getCallerInfo(), []);
  const [doc, setDoc] = useState<ExtendedDoc | null>(null);
  const [, rerenderComponent] = useState({});
  const dispatch = useAppDispatch();
  const previousDataStateRef = useRef<"ready" | "undefined" | null>(null);
  const previousDocRef = useRef<ExtendedDoc | null>(null);
  const currentDocRef = useRef<ExtendedDoc | null>(null);

  // Reset state tracking when docId changes
  useEffect(() => {
    previousDataStateRef.current = null;
    previousDocRef.current = null;
    currentDocRef.current = null;
  }, [docId]);

  // Keep currentDocRef in sync with doc state
  useEffect(() => {
    currentDocRef.current = doc;
  }, [doc]);

  const maybeRerender = useCallback(
    (meta?: DocUpdateMeta) => {
      if (!docId) {
        return;
      }
      // For the case where we listen to a doc before it exists, we need to check if we've loaded
      // it by the time we receive an update.
      const cachedDoc = docCache[docId];
      const currentDoc = cachedDoc || currentDocRef.current;

      // Check if we should re-render based on the check function
      if (checkIfRerenderIsNeeded) {
        const shouldRerender = checkIfRerenderIsNeeded(currentDoc, previousDocRef.current, meta);
        if (!shouldRerender) {
          // Don't re-render, but still pick up a doc that loaded while we were listening.
          if (!currentDocRef.current && cachedDoc) {
            setDoc(cachedDoc);
          }
          previousDocRef.current = currentDoc;
          return;
        }
      }

      // Re-render (either no check function provided, or check returned true)
      rerenderComponent({});
      logInfoWithDebugFlag(
        "debug_sharedb_subscriptions",
        `[useWorksheet] Rerendering with cached doc`,
        {
          docId,
          cachedDocId: cachedDoc?.id,
          caller,
        },
      );
      setDoc((existingDoc) => existingDoc || cachedDoc);
      previousDocRef.current = currentDoc;
    },
    [docId, checkIfRerenderIsNeeded, caller],
  );

  const shareDBContext = useContext(ShareDBContext);
  const shareDBConnection = shareDBContext?.shareDBConnection ?? null;
  const shareDBSectionId = shareDBContext?.shareDBSectionId ?? "";

  const updateDraftPublication = getUpdateDraftPublicationFunction(shareDBSectionId);

  // Cleanup is `return unsubscribe` (destroys doc/presence). Nested setTimeouts inside
  // unsubscribe/flush patches are intentional and not effect-owned registrations.
  // react-doctor-disable-next-line react-doctor/effect-needs-cleanup
  useEffect(() => {
    // We need to ensure we wait until we have a ShareDB connection
    // The connection only exists within a course context (ShareDBProvider is in [courseId].tsx)
    if (!docId || !shareDBConnection || !shareDBSectionId) {
      logInfoWithDebugFlag(
        "debug_sharedb_subscriptions",
        `[useWorksheet] Not subscribing to doc because missing required context`,
        {
          docId,
          shareDBConnection: shareDBConnection?.state,
          shareDBSectionId,
          caller,
        },
      );

      return;
    }

    logInfoWithDebugFlag(
      "debug_sharedb_subscriptions",
      `[useWorksheet] Subscribing to doc ${docId}`,
      {
        docId,
        shareDBConnection: shareDBConnection?.state,
        shareDBSectionId,
        caller,
      },
    );
    const stopListening = listenToDocUpdates(docId, maybeRerender);
    const unsubscribe = () => {
      // Any caller of useWorksheet will listen to a global event emitter for changes to the doc,
      // and the last caller to unsubscribe will actually unsubscribe the doc from the remote server.
      const docToUnsubscribe = docCache[docId];
      stopListening();
      const cleanupSubscriptionAndDoc = () => {
        // Don't delete the cache entry if a new connection already replaced it.
        const cacheStillHoldsThisDoc = docCache[docId] === docToUnsubscribe;
        if (cacheStillHoldsThisDoc && !cacheHasNoListeners(docId)) {
          return;
        }

        if (cacheStillHoldsThisDoc) {
          flushQueue();
          delete docCache[docId];
        }

        logInfoWithDebugFlag(
          "debug_sharedb_subscriptions",
          `[useWorksheet] Unsubscribing from doc`,
          {
            docId,
            caller,
          },
        );

        if (docToUnsubscribe?.localPresence) {
          docToUnsubscribe.localPresence.destroy();
        }
        if (docToUnsubscribe?.presence) {
          docToUnsubscribe.presence.unsubscribe();
        }
        if (docToUnsubscribe) {
          docToUnsubscribe.destroy();
        }
      };
      const shouldReallyUnsubscribe = cacheHasNoListeners(docId);
      if (shouldReallyUnsubscribe) {
        // There are sometimes race condition, where we unsubscribe from a doc, and while we're waiting
        // for server confirmation, the client wants to be subscribed to the doc again (which doesn't send any messages)
        // but the server then processes the unsubscribe request erroneously. As a quick hack,
        // we wait for some time before actually unsubscribing.
        if (isTestEnv) {
          // Tests don't do well with the timeout, as the cleanup of one test
          // might affect the next test, so we disable the timeout in tests.
          cleanupSubscriptionAndDoc();
        } else {
          setTimeout(cleanupSubscriptionAndDoc, 500);
        }
      }

      setDoc(null);
    };

    // The ShareDB code doesn't handle the case well where the same docId is requested
    // multiple times. We cache the instance and make sure future hook calls for the same ID
    // return the same instance, and share the same root listeners as whatever the first instance
    // was that called useWorksheet for that ID.
    // Don't reuse the cached doc when a new connection is created.
    const cachedDoc = docCache[docId];
    if (cachedDoc?.connection === shareDBConnection) {
      logInfoWithDebugFlag("debug_sharedb_subscriptions", `[useWorksheet] Using cached doc`, {
        docId,
        caller,
      });
      setDoc(cachedDoc);
      return unsubscribe;
    }

    const newDoc: ExtendedDoc = shareDBConnection.get(
      window._env_.SHAREDB_WORKSHEET_COLLECTION,
      docId,
    ) as ExtendedDoc;
    logInfoWithDebugFlag("debug_sharedb_subscriptions", `[useWorksheet] Fetched new doc`, {
      docId,
      caller,
    });
    setDoc(newDoc);

    docCache[docId] = newDoc;

    const presence = shareDBConnection.getDocPresence(
      window._env_.SHAREDB_WORKSHEET_COLLECTION,
      docId,
    );
    newDoc.presence = presence;
    newDoc.localPresence = presence.create();
    presence.subscribe();
    newDoc.presence.setMaxListeners(MAX_DOC_LISTENERS);

    const hardRollbackRecoveryTracker = createHardRollbackRecoveryTracker(newDoc);

    const onError = (err: Error & { code?: string }) => {
      if (
        newDoc.hardRollbackStatus === "recovering" &&
        err.code === HARD_ROLLBACK_FETCH_FAILED_CODE
      ) {
        publishHardRollbackStatus(newDoc, "failed");
        hardRollbackRecoveryTracker.clear();
        toastErrorMessage(t("error.toasts.save_failure"), {
          autoClose: false,
        });
      }

      if (isMissingReadPermissionError(err)) {
        const errorState = store.getState();
        const errorPageId = selectPageIdByWorksheetId(errorState, docId);
        const { canViewPage: canViewPageOnError } = selectPageCapabilities(errorState, errorPageId);
        logReadPermissionDenied(
          "[ShareDBDoc] Read permission denied during doc action",
          docId,
          err,
          canViewPageOnError,
          "fetch",
        );
        return;
      }
      rollbarAndLogError("[ShareDBDoc] Error during doc action", { err: err?.message, docId });
    };

    const onOp = (ops?: Op | Op[], source?: unknown) => {
      triggerDocUpdate(docId, ops, source);
    };

    const onLoad = () => {
      onOp();
      if (newDoc.hardRollbackStatus !== "recovering") return;

      publishHardRollbackStatus(newDoc, "recovered");
      hardRollbackRecoveryTracker.complete();
    };

    const onDel = () => unsubscribe();

    newDoc.on("op", onOp);
    newDoc.on("load", onLoad);
    newDoc.on("del", onDel);
    newDoc.on("error", onError);
    newDoc.setMaxListeners(MAX_DOC_LISTENERS);

    // Add event listeners for submission tracking and field text loss detection
    newDoc.on("before op batch", (ops: ExtendedOp[]) =>
      updateSubmissionTrackerState(newDoc, ops, "beforeOpBatch"),
    );
    newDoc.on("before op", (ops: ExtendedOp[], source: unknown) => {
      updateSubmissionTrackerState(newDoc, ops, "beforeOp");
      // ShareDB uses source === false for server-applied ops; only track local submissions.
      if (newDoc.data && source !== false) {
        trackLocalOpFieldTextBeforeApply(newDoc.id, newDoc.data, ops);
      }
    });
    newDoc.on("op", (ops: ExtendedOp[], source: unknown) => {
      updateSubmissionTrackerState(newDoc, ops, "op");
      if (newDoc.data && source !== false) {
        trackLocalOpFieldTextAfterApply(newDoc.data, ops);
      }
    });

    if (!newDoc.__hardRollback) {
      // Override _hardRollback to track failures
      newDoc.__hardRollback = newDoc._hardRollback;

      // This event can happen on an op submission, but when it does it rolls back the submission
      // without sending it to the server and doesn't necessarily log anything.  We are patching it
      // here so we can at least log it (and disable the submission tracker as we know it failed).
      newDoc._hardRollback = function _hardRollback(this: ExtendedDoc, error: Error) {
        const operationId = getOperationId(this.inflightOp);
        const callbackCounts = getHardRollbackCallbackCounts(this);
        const state = store.getState();
        if (this.hardRollbackStatus === "recovering") {
          logInfoWithDebugFlag("debug_sharedb", "[ShareDBDoc] Overlapping hard rollback detected", {
            docId: this.id,
            operationId,
            error: error.message,
          });
        }
        // ShareDB later passes this error to every discarded op callback, which can cause a flood
        // of duplicate logs. Mark it before delegating so callback layers skip those reports while
        // this rollback still logs.
        markShareDBErrorReported(error);
        hardRollbackRecoveryTracker.start(operationId, error.message);
        publishHardRollbackStatus(this, "recovering");

        // ShareDB's _hardRollback calls _setType(null) which sets doc.data to undefined.
        // Preserve stale data so React components don't crash during the async fetch.
        // ingestSnapshot will properly replace it with fresh data on completion.
        const staleData = this.data;

        if (newDoc.__hardRollback) {
          newDoc.__hardRollback.call(this, error);
        }

        if (!this.data && staleData) {
          this.data = staleData;
        }

        const fieldLosses =
          this.data &&
          reportUncommittedWorksheetFieldTextLoss({
            docId: this.id,
            doc: this.data,
            operationId,
            relatedOperationIds: getRelatedOperationIds(operationId),
            cause: "rollback",
            userId: selectCurrentUserId(state),
            sectionId: shareDBSectionId,
            errorMessage: error.message,
          });

        rollbarAndLogError(
          "ShareDB _hardRollback called - operation failed",
          enrichShareDBFailureLog(
            {
              docId: this.id,
              error: error.message,
              errorStack: error.stack,
              connectionState: this.connection?.state,
              canSend: this.connection?.canSend,
              operationId,
              ...callbackCounts,
            },
            fieldLosses ?? [],
          ),
        );

        if (operationId) {
          clearSubmissionTrackerById(operationId, { cause: "rollback" });
        }
        reconcileSubmissionTrackersForDoc(this, "hardRollback");
      };
    }

    // The flush mechanism is not resending ops if they never get acknowledged.
    // We are patching here to do that but also adding a timeout to ensure
    // we don't wait indefinitely for an acknowledgment that may never come.
    // Here's what the original flush method looks like:
    // ```
    // // Send the next pending op to the server, if we can.
    // //
    // // Only one operation can be in-flight at a time. If an operation is already on
    // // its way, or we're not currently connected, this method does nothing.
    // Doc.prototype.flush = function () {
    //   // Ignore if we can't send or we are already sending an op
    //   if (!this.connection.canSend || this.inflightOp) return;
    //   // Send first pending op unless paused
    //   if (!this.paused && this.pendingOps.length) {
    //     this._sendOp();
    //   }
    // };
    // ```
    if (!newDoc._flush) {
      newDoc._flush = newDoc.flush;
      newDoc.flush = function flush(this: ExtendedDoc) {
        // Ignore if we can't send
        if (!this.connection.canSend) return;

        // Send first pending op unless paused
        if (!this.paused && (this.pendingOps.length || this.inflightOp)) {
          // Here we add some logic to detect if the op is stuck in flight too long
          if (this.inflightOp) {
            // Calculate exponential backoff delay: 1s, 2s, 4s, 8s, 16s, etc.
            const retryDelay = Math.min(
              MINIMUM_TIME_BETWEEN_REQUESTS_MS * 2 ** this.inflightOp.retries,
              MAX_RETRY_DELAY_MS,
            ); // Cap at 30 seconds
            // If we're inflight and it hasn't been long enough based on retry count, skip resending
            if (
              this.inflightOp.sentAt &&
              !isPast(addMilliseconds(this.inflightOp.sentAt, retryDelay))
            ) {
              return;
            }

            if (!this.inflightOp.originalSentAt) {
              this.inflightOp.originalSentAt = this.inflightOp.sentAt;

              // The first time we get here set a timeout
              setTimeout(() => {
                // Check if its the same inflight op
                if (
                  this.inflightOp &&
                  isPast(
                    addMilliseconds(
                      this.inflightOp.originalSentAt,
                      DELAYED_SAVE_WARNING_THRESHOLD_MS,
                    ),
                  )
                ) {
                  toast.error(t("error.toasts.save_delay"));
                }
              }, DELAYED_SAVE_WARNING_THRESHOLD_MS);
            }

            logInfoWithDebugFlag("debug_sharedb", "ShareDB op stuck in flight - resending", {
              originalSentAt: this.inflightOp.originalSentAt,
              retries: this.inflightOp.retries,
              docId: this.id,
              seq: this.inflightOp.seq,
              operationId: getOperationId(this.inflightOp),
            });

            if (
              this.inflightOp.retries > MAX_RETRIES ||
              isPast(addMinutes(this.inflightOp.originalSentAt, MAX_MINUTES_IN_FLIGHT))
            ) {
              rollbarAndLogError("ShareDB op stuck in flight too long", {
                originalSentAt: this.inflightOp.originalSentAt,
                retries: this.inflightOp.retries,
                docId: this.id,
              });

              // Clear the op that is stuck
              this._clearInflightOp();

              toast.error(t("error.toasts.save_failure"));
            }
          }

          this._sendOp();
        }
      };
    }

    // // TODO: You can comment this back in if you want to test acknowledgments
    // // that fail every other time
    // // Global variable to simulate intermittent failures
    // if (typeof window !== "undefined" && !window.__shareDBTestFailEveryOther) {
    //   window.__shareDBTestFailEveryOther = true;
    // }

    // if (!newDoc.__opAcknowledged) {
    //   newDoc.__opAcknowledged = newDoc._opAcknowledged;

    //   // Override _opAcknowledged to simulate intermittent failures
    //   newDoc._opAcknowledged = function _opAcknowledged(this: ExtendedDoc, message: any) {
    //     // Toggle the failure flag every time
    //     if (typeof window !== "undefined") {
    //       window.__shareDBTestFailEveryOther = !window.__shareDBTestFailEveryOther;
    //     }

    //     // If the flag is false, simulate failure by returning early
    //     if (typeof window !== "undefined" && !window.__shareDBTestFailEveryOther) {
    //       console.log("debug_sharedb_silent_failures", "Simulating opAcknowledged failure", {
    //         docId: this.id,
    //         message,
    //         failFlag: window.__shareDBTestFailEveryOther,
    //       });
    //       return; // Simulate failure by not calling the original method
    //     }

    //     // Call the original _opAcknowledged method
    //     console.log("debug_sharedb_silent_failures", "Allowing opAcknowledged to proceed", {
    //       docId: this.id,
    //       message,
    //       failFlag: window.__shareDBTestFailEveryOther,
    //     });

    //     return newDoc.__opAcknowledged.call(this, message);
    //   };
    // }

    // The ShareDB `doc` object is actually a function (not a class), which means we can't substitute
    // and methods on individual instances, rather, the changes below affect _all_ current and future
    // doc instances, hence, we make sure to only do it once.
    type ShareDBDocPrototype = Doc & {
      __submissionTrackerTryComposePatched?: boolean;
      _tryCompose?: (op: ExtendedOp) => ExtendedOp | undefined;
    };
    const docPrototype = Doc.prototype as ShareDBDocPrototype;
    if (!docPrototype.__submissionTrackerTryComposePatched) {
      const originalTryCompose = docPrototype._tryCompose;
      if (!originalTryCompose) {
        throw new Error("ShareDB Doc.prototype._tryCompose is missing");
      }
      docPrototype._tryCompose = function tryComposeWithSubmissionTracking(
        this: ExtendedDoc,
        op: ExtendedOp,
      ): ExtendedOp | undefined {
        const composed = originalTryCompose.call(this, op) as ExtendedOp | undefined;
        if (composed) {
          const mergedOperationId = getOperationId(op);
          const survivorOperationId = getOperationId(composed);
          if (
            mergedOperationId &&
            survivorOperationId &&
            mergedOperationId !== survivorOperationId
          ) {
            handleSubmissionComposed(mergedOperationId, survivorOperationId, this);
          }
        }
        return composed;
      };
      docPrototype.__submissionTrackerTryComposePatched = true;
    }

    if (!newDoc._submitOp) {
      // eslint-disable-next-line @typescript-eslint/unbound-method
      newDoc._submitOp = newDoc.submitOp;

      newDoc.submitOp = function submitOp(
        this: ExtendedDoc,
        op: ExtendedOp | ExtendedOp[],
        options: ShareDBSourceOptions,
        callback: Callback,
      ) {
        if (typeof options === "function") {
          throw new Error("second argument to submitOp can not be a callback");
        }

        // doc.type is null in three cases: (1) before the initial snapshot arrives from the
        // server, (2) during _hardRollback recovery while the re-fetch is in flight, and
        // (3) after the doc is deleted. Submitting in any of these states would throw
        // ERR_DOC_DOES_NOT_EXIST in ShareDB's _submit — the op can't apply and would be lost
        // anyway, so skip to avoid spurious errors.
        if (!this.type) {
          logInfoWithDebugFlag("debug_sharedb", "submitOp skipped: doc not ready", {
            docId,
            op,
          });
          return;
        }

        // Create submission tracker for this operation
        const operationId = `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

        // Add unique ID to the operation
        const ops = Array.isArray(op) ? op : [op];
        ops.forEach((operation) => {
          if (!operation.operationId) {
            // eslint-disable-next-line no-param-reassign
            operation.operationId = operationId;
          }
        });

        checkOpPermissions(newDoc, op);

        // Here we start a submission tracker to ensure we can track the status of this operation
        // and throw an error if it never completes.
        createSubmissionTracker(this, operationId);

        logInfoWithDebugFlag("debug_sharedb_silent_failures", `Tracker created,`, {
          operationId,
          op,
        });

        // Log and rethrow to verify whether this  dispatch is the source of
        // React error #185 without changing the existing submit behavior.
        // https://app.rollbar.com/a/minervaproject/fix/item/Monarch/21086#detail
        try {
          dispatch(viewStateActions.onSendShareDBOp());
        } catch (error: unknown) {
          logInfoWithDebugFlag("debug_sharedb", "[ShareDBDoc] onSendShareDBOp dispatch threw", {
            docId: this.id,
            operationId,
            error,
          });
          throw error;
        }

        this._submitOp(op, options, (error: WebsocketErrorType) => {
          if (error) {
            const state = store.getState();
            const fieldLosses =
              this.data &&
              reportUncommittedWorksheetFieldTextLoss({
                docId: this.id,
                doc: this.data,
                operationId,
                relatedOperationIds: getRelatedOperationIds(operationId),
                cause: "submit-error",
                userId: selectCurrentUserId(state) ?? state.auth.websocket?.user_id ?? undefined,
                sectionId: shareDBSectionId ?? undefined,
                errorMessage: error.message,
              });

            reportShareDBErrorOnce(error, () => {
              rollbarAndLogError(
                "[ShareDBDoc] Error submitting op for doc",
                enrichShareDBFailureLog(
                  { docId, operationId, error: error.message },
                  fieldLosses ?? [],
                ),
              );
            });
            dispatch(viewStateActions.onShareDBError(error.message));
            clearSubmissionTrackerById(operationId, { cause: "submit-error" });
          } else {
            // Skip draft updates for access-ID initialization ops (oid). These are
            // housekeeping ops that don't represent content changes and would otherwise
            // create phantom draft publications when worksheets are first opened.
            const opsArray = Array.isArray(op) ? op : [op];
            const isAccessIdInit = opsArray.every((o) => "oid" in o);
            if (!isAccessIdInit) {
              updateDraftPublication();
            }
          }

          dispatch(viewStateActions.onReceiveShareDBOp());

          incrementVersion(this, op);

          if (callback) {
            callback(error);
          }
        });
      };
    }

    const state = store.getState();
    const pageId = selectPageIdByWorksheetId(state, docId);
    const { canViewPage } = selectPageCapabilities(state, pageId);
    newDoc.subscribe((err) => {
      if (err) {
        if (isMissingReadPermissionError(err)) {
          logReadPermissionDenied(
            "[ShareDBDoc] Read permission denied while subscribing to doc",
            docId,
            err,
            canViewPage,
            "subscribe",
          );
        } else {
          const websocket = store.getState().auth.websocket;
          const reason = getDocErrorReason(docId, canViewPage, "subscribe");
          rollbarAndLogError("[ShareDBDoc] Error subscribing to doc", {
            docId,
            user_id: websocket?.user_id,
            err: err.message,
            reason,
          });
        }
      } else if (newDoc.type === null) {
        rollbarAndLogWarning("[ShareDBDoc] doc.subscribe called but no type set", { docId });
      }
      logInfoWithDebugFlag(
        "debug_sharedb_subscriptions",
        `[ShareDBDoc] Subscribed to doc ${docId}`,
      );
    });

    return unsubscribe;
  }, [
    docId,
    shareDBConnection,
    shareDBSectionId,
    dispatch,
    maybeRerender,
    updateDraftPublication,
    caller,
  ]);

  const docReadyState: "ready" | "undefined" | "null" =
    doc && doc.data ? "ready" : doc && !doc.data ? "undefined" : "null";

  // Log data-state transitions once per change (kept out of render for purity).
  useEffect(() => {
    if (docReadyState === "undefined") {
      if (previousDataStateRef.current !== "undefined") {
        logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc data is undefined`, {
          docId,
          caller,
        });
        previousDataStateRef.current = "undefined";
      }
      return;
    }

    if (docReadyState === "ready") {
      if (previousDataStateRef.current !== "ready") {
        logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc is ready`, {
          docId,
          caller,
        });
        previousDataStateRef.current = "ready";
      }
      return;
    }

    if (previousDataStateRef.current !== null) {
      logInfoWithDebugFlag("debug_sharedb", `[useWorksheet] Doc is null`, {
        docId,
        caller,
      });
    }
    previousDataStateRef.current = null;
  }, [docReadyState, docId, caller]);

  if (docReadyState === "undefined") {
    // If the doc breaks in unexpected ways, doc.data will be undefined.
    // TODO: We might want to separate the two cases to distinguish between error and loading states.
    return { doc: null };
  }

  if (docReadyState === "ready") {
    return { doc };
  }

  return { doc: null };
};

// ============================================================================
// Change Detection Helpers
// ============================================================================
// These functions determine when a component should re-render based on specific
// changes to the ShareDB document. They are used by specialized hooks to optimize
// re-rendering behavior.

/**
 * Helper function to check if blocks structure changed (blocks added/removed/type changed).
 * This ignores content changes within blocks.
 */
const blocksStructureChanged = (
  currentDoc: Doc<WorksheetType> | null,
  previousDoc: Doc<WorksheetType> | null,
): boolean => {
  if (!currentDoc?.data || !previousDoc?.data) {
    // If either doc is missing, consider it a change (initial load or doc deleted)
    return currentDoc?.data !== previousDoc?.data;
  }

  const currentBlocks = currentDoc.data.b.map((b) => ({ id: b.id, t: b.t }));
  const previousBlocks = previousDoc.data.b.map((b) => ({ id: b.id, t: b.t }));

  if (currentBlocks.length !== previousBlocks.length) {
    return true;
  }

  // Check if any block IDs or types changed
  return !currentBlocks.every(
    (block, index) =>
      block.id === previousBlocks[index]?.id && block.t === previousBlocks[index]?.t,
  );
};

/**
 * Helper function to check if hasData for a specific accessId changed or if blocks changed.
 * Blocks changes matter because they affect which fields exist (and thus which data is considered valid).
 * Only re-renders when hasData changes (true -> false or false -> true), not when data changes but hasData stays the same.
 */
const accessIdHasDataChanged =
  (accessId: string) =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    // If blocks changed, we need to re-render (affects fieldIds, which affects hasData)
    if (blocksStructureChanged(currentDoc, previousDoc)) {
      return true;
    }

    if (!currentDoc?.data || !previousDoc?.data) {
      return currentDoc?.data !== previousDoc?.data;
    }

    // Check if hasData changed (not just if data changed)
    const currentHasData = accessIdHasData(currentDoc, accessId);
    const previousHasData = accessIdHasData(previousDoc, accessId);

    return currentHasData !== previousHasData;
  };

/**
 * Check if doc loaded state changed (null -> doc or doc -> null).
 * Stable reference — safe to pass directly as checkIfRerenderIsNeeded.
 */
const docLoadedChanged = (
  currentDoc: Doc<WorksheetType> | null,
  previousDoc: Doc<WorksheetType> | null,
): boolean => {
  const currentLoaded = Boolean(currentDoc?.data);
  const previousLoaded = Boolean(previousDoc?.data);
  return currentLoaded !== previousLoaded;
};

/**
 * Helper function to check if doc version has diverged from a reference version.
 * Only re-renders when the divergence status changes (true -> false or false -> true),
 * not when doc version changes but the divergence status stays the same.
 */
const docVersionDivergedChanged =
  (referenceVersion: number | undefined) =>
  (currentDoc: Doc<WorksheetType> | null, previousDoc: Doc<WorksheetType> | null): boolean => {
    if (!referenceVersion) {
      // No reference version, so divergence is always false
      return false;
    }

    const currentDiverged = (currentDoc?.version ?? 0) !== referenceVersion;
    const previousDiverged = (previousDoc?.version ?? 0) !== referenceVersion;

    return currentDiverged !== previousDiverged;
  };

/**
 * Check if hasBlocks changed.
 * Only re-renders when blocks are added/removed (not when content changes).
 * Stable reference — safe to pass directly as checkIfRerenderIsNeeded.
 */
const hasBlocksChanged = (
  currentDoc: Doc<WorksheetType> | null,
  previousDoc: Doc<WorksheetType> | null,
): boolean => {
  if (!currentDoc?.data || !previousDoc?.data) {
    return currentDoc?.data !== previousDoc?.data;
  }

  const currentHasBlocks = hasBlocks(currentDoc);
  const previousHasBlocks = hasBlocks(previousDoc);

  return currentHasBlocks !== previousHasBlocks;
};

/**
 * Check if accessIds in worksheet.d changed (keys added/removed).
 * Only re-renders when accessIds change, not when data within accessIds changes.
 * Stable reference — safe to pass directly as checkIfRerenderIsNeeded.
 */
const worksheetAccessIdsChanged = (
  currentDoc: Doc<WorksheetType> | null,
  previousDoc: Doc<WorksheetType> | null,
): boolean => {
  if (!currentDoc?.data || !previousDoc?.data) {
    return currentDoc?.data !== previousDoc?.data;
  }

  const currentAccessIds = new Set(Object.keys(currentDoc.data[DATA_PATH] || {}));
  const previousAccessIds = new Set(Object.keys(previousDoc.data[DATA_PATH] || {}));

  if (currentAccessIds.size !== previousAccessIds.size) {
    return true;
  }

  return ![...currentAccessIds].every((id) => previousAccessIds.has(id));
};

// ============================================================================
// Specialized Hooks
// ============================================================================
// These hooks subscribe to worksheet changes but only re-render when the specific
// value they return changes, not on every ShareDB operation.

/**
 * Subscribe to a derived value on an already-loaded ShareDB doc.
 * ShareDB mutates `doc` in place, so the value is cached and compared with Object.is —
 * return a primitive (or otherwise stable result), not a new object each call.
 */
export const useDocSelectedValue = <T,>(
  doc: Doc<WorksheetType> | null | undefined,
  getSelectedValue: (doc: Doc<WorksheetType> | null | undefined) => T,
): T => {
  const selectedValue = getSelectedValue(doc);
  const [, rerender] = useState({});

  const onDocUpdate = useEffectEvent(() => {
    const next = getSelectedValue(doc);
    if (Object.is(selectedValue, next)) return;
    rerender({});
  });

  useEffect(() => {
    if (!doc?.id) return undefined;
    return listenToDocUpdates(doc.id, onDocUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useEffectEvent identity is unstable
  }, [doc]);

  return selectedValue;
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when doc loaded state changes.
 * Returns whether the doc is loaded (has data).
 * Useful for components that only care about when the doc finishes loading.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetLoaded = (docId: string | undefined): boolean => {
  const { doc } = useWorksheet(docId, docLoadedChanged);
  return Boolean(doc?.data);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when hasData for a specific accessId changes
 * (true -> false or false -> true) or when blocks change (which affects which fields exist).
 * Returns whether the specified accessId has data.
 * Useful for components that only care about whether a specific accessId has data.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useAccessIdHasData = (docId: string | undefined, accessId: string): boolean | null => {
  const checkFn = useMemo(() => accessIdHasDataChanged(accessId), [accessId]);
  const { doc } = useWorksheet(docId, checkFn);
  if (!doc) return null;
  return accessIdHasData(doc, accessId);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when hasBlocks changes
 * (true -> false or false -> true), not when content within blocks changes.
 * Returns whether the worksheet has any blocks.
 * Useful for components that only care about whether the doc has blocks.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetHasBlocks = (docId: string | undefined): boolean | null => {
  const { doc } = useWorksheet(docId, hasBlocksChanged);
  if (!doc) return null;
  return hasBlocks(doc);
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when accessIds in worksheet.d change
 * (keys added/removed), not when data within those accessIds changes.
 * Returns the accessIds (keys of worksheet.d).
 * Useful for components that only care about which accessIds exist, not their content.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useWorksheetAccessIds = (docId: string | undefined | null): string[] => {
  const { doc } = useWorksheet(docId, worksheetAccessIdsChanged);
  if (!doc?.data) return [];
  return Object.keys(doc.data[DATA_PATH] || {});
};

/**
 * Hook that subscribes to worksheet changes but only re-renders when doc version divergence changes
 * (true -> false or false -> true), not when doc version changes but the divergence status stays the same.
 * Returns whether the doc version has diverged from the reference version.
 * Useful for components that only care about whether the doc has been edited since a reference point.
 * If you need the doc for other purposes, use `useWorksheet` instead.
 */
export const useDocVersionDiverged = (
  docId: string | null,
  referenceVersion: number | undefined,
): boolean => {
  const checkFn = useMemo(() => docVersionDivergedChanged(referenceVersion), [referenceVersion]);
  const { doc } = useWorksheet(docId, checkFn);
  if (!referenceVersion) return false;
  return (doc?.version ?? 0) !== referenceVersion;
};

// ============================================================================
// Other Hooks
// ============================================================================

type WorksheetPermissionSnapshot = {
  canViewOtherUsersData: boolean;
  pageGroupId: string | null;
  classResponsesReleasedAt: string | null;
  syncOtherUsersData: boolean;
};

/**
 * Featuring a rollup sets page.last_rolled_up_at, which flips canViewOtherUsersData for students.
 * forceRefreshDoc nulls ShareDB type/data and unmounts Blocks. Non-followers should keep their
 * own worksheet; followers and class-response releases still need the refetch. Presenters already
 * have canViewOtherUsersData, so they must not refetch on start-present.
 */
export const shouldRefreshWorksheetForPermissionChange = (
  previousPermissions: WorksheetPermissionSnapshot,
  currentPermissions: WorksheetPermissionSnapshot,
) => {
  if (
    previousPermissions.pageGroupId !== currentPermissions.pageGroupId ||
    previousPermissions.classResponsesReleasedAt !== currentPermissions.classResponsesReleasedAt
  ) {
    return true;
  }

  if (!currentPermissions.syncOtherUsersData) {
    return false;
  }

  const canViewOtherUsersDataChanged =
    previousPermissions.canViewOtherUsersData !== currentPermissions.canViewOtherUsersData;
  const syncBecameActive =
    !previousPermissions.syncOtherUsersData && currentPermissions.syncOtherUsersData;

  return (
    canViewOtherUsersDataChanged || (syncBecameActive && currentPermissions.canViewOtherUsersData)
  );
};

/**
 * Listens to permission changes and forces a data refresh on the document if the permissions
 * change in a meaningful way.
 */
export const useWorksheetPermissionRefresh = (
  pageId: string,
  { syncOtherUsersData = true }: { syncOtherUsersData?: boolean } = {},
) => {
  const docId = useAppSelector((s) => selectWorksheetIdByPage(s, pageId));

  const { canViewPage, canViewOtherUsersData } = usePageCapabilitiesForPageId(pageId);
  const pageGroupId = useAppSelector((s) => selectPageGroupIdForCurrentUserByPageId(s, pageId));
  const classResponsesReleasedAt = useAppSelector(
    (s) => pageSelectors.selectById(s, pageId)?.class_responses_released_at ?? null,
  );

  const previousPermissionsRef = useRef<WorksheetPermissionSnapshot>({
    canViewOtherUsersData,
    pageGroupId,
    classResponsesReleasedAt,
    syncOtherUsersData,
  });

  const dispatch = useAppDispatch();
  const refreshTokenRef = useRef(0);

  // Use a layout effect so we can mark "refresh in-flight" before passive effects (e.g. worksheet init ops)
  // run in the same commit.
  useLayoutEffect(() => {
    const permissions = {
      canViewOtherUsersData,
      pageGroupId,
      classResponsesReleasedAt,
      syncOtherUsersData,
    };

    const previousPermissions = previousPermissionsRef.current;

    if (!docId || !canViewPage) {
      previousPermissionsRef.current = permissions;
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Not refreshing doc because not released or no docId`,
        {
          docId,
          canViewPage,
        },
      );

      return;
    }

    const doc = docCache[docId];

    if (!doc || doc.id !== docId) {
      previousPermissionsRef.current = permissions;
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Not refreshing doc because no doc or docId changed`,
        {
          docId,
          doc,
        },
      );

      return;
    }

    if (shouldRefreshWorksheetForPermissionChange(previousPermissions, permissions)) {
      logInfoWithDebugFlag(
        "debug_sharedb",
        `[useWorksheetPermissionRefresh] Permissions changed, forcing refresh`,
        {
          docId,
          previousPermissions,
          permissions,
        },
      );

      refreshTokenRef.current += 1;
      const token = refreshTokenRef.current;

      dispatch(viewStateActions.setWorksheetIsRefreshing(true));

      forceRefreshDoc(docId, () => {
        // Don't clear if another refresh started after this one.
        if (refreshTokenRef.current === token) {
          dispatch(viewStateActions.setWorksheetIsRefreshing(false));
        }
      });
    }

    previousPermissionsRef.current = permissions;
  }, [
    docId,
    dispatch,
    canViewPage,
    canViewOtherUsersData,
    pageGroupId,
    classResponsesReleasedAt,
    syncOtherUsersData,
  ]);
};
